import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import { AUTOMATION_QUERY_KEYS } from '../queryKeys';

function collectChildTemplateIdsFromSteps(steps?: Api.IAutomationStep[]): string[] {
	const childTemplateIds = new Set<string>();

	for (const step of steps ?? []) {
		const cases = (step as Api.ISwitchAutomationStep | undefined)?.cases;
		for (const caseStatement of cases ?? []) {
			if (caseStatement.automationTemplateId) {
				childTemplateIds.add(caseStatement.automationTemplateId);
			}
		}
	}

	return [...childTemplateIds];
}

function collectChildTemplateIds(template: Api.IAutomationTemplate | undefined): string[] {
	if (!template) {
		return [];
	}

	const childTemplateIds = new Set<string>();
	for (const templateId of collectChildTemplateIdsFromSteps(template.draft?.steps)) {
		childTemplateIds.add(templateId);
	}
	for (const templateId of collectChildTemplateIdsFromSteps(template.published?.steps)) {
		childTemplateIds.add(templateId);
	}
	return [...childTemplateIds];
}

function buildTemplateTreeSignature(template: Api.IAutomationTemplate | undefined): string {
	if (!template?.id) {
		return 'missing-template';
	}

	const draftChildTemplateIds = collectChildTemplateIdsFromSteps(template.draft?.steps).sort().join(',');
	const publishedChildTemplateIds = collectChildTemplateIdsFromSteps(template.published?.steps).sort().join(',');

	return [
		template.id,
		template.draft ? 'has-draft' : 'no-draft',
		template.published ? 'has-published' : 'no-published',
		`draft-children:${draftChildTemplateIds}`,
		`published-children:${publishedChildTemplateIds}`,
	].join('|');
}

async function fetchAutomationTemplate(
	userSession: ReturnType<typeof useUserSession>,
	templateId: string,
	impersonationContext?: Api.IImpersonationContext
): Promise<Api.IAutomationTemplate | null> {
	try {
		return await userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
			Api.ImpersonationBroker.composeApiUrl({
				urlPath: `automationTemplate/${encodeURIComponent(templateId)}`,
				impersonationContext,
			}),
			'GET'
		);
	} catch (error) {
		console.error(`[useAutomationTemplateHasDraftQuery] Failed to fetch child template ${templateId}`, error);
		return null;
	}
}

async function templateTreeHasDraft(
	template: Api.IAutomationTemplate,
	userSession: ReturnType<typeof useUserSession>,
	impersonationContext: Api.IImpersonationContext | undefined,
	visitedTemplateIds: Set<string>
): Promise<boolean> {
	if (template.draft != null) {
		return true;
	}

	visitedTemplateIds.add(template.id);

	for (const childTemplateId of collectChildTemplateIds(template)) {
		if (visitedTemplateIds.has(childTemplateId)) {
			continue;
		}

		const childTemplate = await fetchAutomationTemplate(userSession, childTemplateId, impersonationContext);
		if (!childTemplate) {
			continue;
		}

		if (await templateTreeHasDraft(childTemplate, userSession, impersonationContext, visitedTemplateIds)) {
			return true;
		}
	}

	return false;
}

export function useAutomationTemplateHasDraftQuery({
	template,
	enabled = true,
	impersonationContext,
}: {
	template: Api.IAutomationTemplate | undefined;
	enabled?: boolean;
	impersonationContext?: Api.IImpersonationContext;
}) {
	const userSession = useUserSession();
	const templateTreeSignature = useMemo(() => buildTemplateTreeSignature(template), [template]);

	return useQuery({
		enabled: enabled && !!template?.id,
		queryKey: [
			...AUTOMATION_QUERY_KEYS.templateDraftState(template?.id || ''),
			templateTreeSignature,
			impersonationContext?.account?.id,
			impersonationContext?.user?.id,
		],
		queryFn: async () => {
			if (!template) {
				return false;
			}

			return templateTreeHasDraft(template, userSession, impersonationContext, new Set<string>());
		},
	});
}

export function invalidateAutomationTemplateDraftStateQuery(templateId: string) {
	reactQueryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.templateDraftState(templateId) });
}
